import React, { useContext, useEffect, useState } from 'react';
import validator from '../../utils/FormValidator';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { FormContext, FormSchemaFields } from '@sw-sw/lib-form';
import { FormModal } from '../Shared/form';
import tenantApi from '../../utils/api/tenant';
import ToggleSmall from '../Shared/Toggle/ToggleSmall';
import { ActionButtons } from '../Shared/ActionButtons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import subscriptionApi from '../../utils/api/subscription';
import { Modal } from '@sw-sw/lib-ui';
import { Tooltip } from '@mui/material';
import rolesApi from '../../utils/api/roles';
import { toast } from 'react-toastify';

function getSchema(windowWidth: number, showRoles: boolean, roles: any[]) {
  return {
    name: {
      label: 'Name',
      validation: {
        required: true,
      },
      style: {
        flexBasis: windowWidth > 768 ? '40%' : '100%',
      },
    },
    price: {
      label: 'Price',
      parse: validator.parseDecimal,
      validation: {
        required: true,
      },
      style: {
        flexBasis: windowWidth > 768 ? '20%' : '100%',
      },
    },
    is_public: {
      label: 'Available to Public',
      controlType: UIControlType.select,
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
      validation: {
        required: true,
      },
      style: {
        flexBasis: windowWidth > 768 ? '20%' : '100%',
      },
    },
    subscription_type: {
      label: 'Subscription Type',
      controlType: UIControlType.select,
      options: [
        { value: 'project_based', label: 'Project Based' },
        { value: 'user_based', label: 'User Based' },
      ],
      validation: {
        required: true,
      },
      style: {
        flexBasis: windowWidth > 768 ? '20%' : '100%',
      },
    },
    description: {
      label: 'Description',
      controlType: UIControlType.richText,
      parse: validator.parseRichText,
      validation: {
        required: true,
      },
    },
    subscriptionRoles: {
      label: 'Add Allowed Roles',
      controlType: UIControlType.customTagSelect,
      labelKey: 'name',
      valueKey: 'role_id',
      isMulti: true,
      showSelectAll: true,
      openOnFocus: true,
      options: roles,
      placeholder: 'Search for Roles',
      style: {
        display: showRoles ? '' : 'none',
      },
    },
  };
}

function Features({
  title,
  featuresList,
  handleToggle,
}: {
  title: string;
  featuresList: any[];
  handleToggle: (id: number) => void;
}) {
  return (
    <div className='subscription-each-feaures-container'>
      <div className='subscription-features-header'>
        <h3 style={{ marginBottom: '0px' }}>{title}</h3>
      </div>
      <div style={{ marginBottom: '10px' }}>
        {featuresList.map((ele, index) => {
          return (
            <div className='subscription-table-body' key={index}>
              <h4 style={{ padding: '0px' }}>{ele.name}</h4>
              <h4
                style={{ padding: '0px' }}
                className='subscription-table-body__toggle'
              >
                <ToggleSmall
                  isChecked={ele.is_enabled}
                  handleChange={() => handleToggle(ele.id)}
                />
              </h4>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SubscriptionModal({
  isEditAction,
  setShowModal,
  selectedSubscription,
  setShowAddOnModal,
  addOnList,
  setAddOnList,
  setSelectedAddOnModalDetails,
  setShowUserRoleModal,
  userRolesList,
  setSelectedAddUserRoleModalDetails,
  subscriptionQuery,
}: {
  isEditAction: boolean;
  setShowModal: (show: boolean) => void;
  selectedSubscription: any;
  setShowAddOnModal: (show: boolean) => void;
  addOnList: any;
  setAddOnList: (list: any) => void;
  setSelectedAddOnModalDetails: (details: any) => void;
  setShowUserRoleModal: (show: boolean) => void;
  userRolesList: any;
  setSelectedAddUserRoleModalDetails: (setDetails: any) => void;
  subscriptionQuery: any;
}) {
  const formContext = useContext(FormContext);

  const [initialFeatures, setInitialFeatures] = useState<any>([]);
  const [isUserBased, setIsUserBased] = useState<boolean>(false);
  const [addOnToggle, setAddOnToggle] = useState<boolean>(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [mappingRoles, setMappingRoles] = useState<any>([]);
  const [usedByTenantList, setUsedByTenantList] = useState<any>([]);

  useEffect(() => {
    rolesApi.getAllRoles().then((res) => {
      const rolesWithRoleId = res.map((role: any) => {
        return {
          name: role.name,
          role_id: role.id,
        };
      });
      setMappingRoles(rolesWithRoleId);
    });
  }, []);

  useEffect(() => {
    if (isEditAction) {
      tenantApi.features.getAllFeaturesName().then((res) => {
        const features = res.map((ele: any) => {
          const selectedFeature = selectedSubscription.features.find(
            (f: any) => f.feature_id === ele.id,
          );
          return {
            ...ele,
            is_enabled: selectedFeature ? selectedFeature.is_enabled : false,
          };
        });
        setInitialFeatures(features);
      });
      setAddOnToggle(selectedSubscription.is_addon);
    } else {
      tenantApi.features.getAllFeaturesName().then((res) => {
        const features = res.map((ele: any) => {
          return {
            ...ele,
            is_enabled: false,
          };
        });
        setInitialFeatures(features);
      });
    }
    setSelectedAddOnModalDetails(null);
  }, []);

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (formContext.value.subscription_type === 'user_based') {
      setIsUserBased(true);
    } else if (formContext.value.subscription_type === 'project_based') {
      setIsUserBased(false);
    }
  }, [formContext.value.subscription_type]);

  const getRoleName = (role_id: number) => {
    const role = mappingRoles.find(
      (ele: any) => ele.role_id === Number(role_id),
    );
    return role ? role.name : 'Unknown';
  };

  const handleCreate = async (formData: any) => {
    const data = {
      ...formData,
      description: validator.parseRichTextAtSubmit(formData.description),
      features: initialFeatures,
      is_addon: isUserBased ? false : addOnToggle,
      addOnList: isUserBased ? [] : addOnList,
      userRoleList: isUserBased ? userRolesList : [],
      roles: formData.subscriptionRoles,
    };
    if (isUserBased) delete data.roles;
    subscriptionApi.create(data).then(() => {
      subscriptionQuery.refetch();
      toast.success('Subscription Created Successfully!');
    });
    setShowModal(false);
  };

  const handleEdit = async (formData: any) => {
    delete formData.addons;
    delete formData.createdAt;
    delete formData.updatedAt;
    delete formData.deletedAt;
    const data = {
      ...formData,
      description: validator.parseRichTextAtSubmit(formData.description),
      features: initialFeatures,
      is_addon: isUserBased ? false : addOnToggle,
      addOnList: isUserBased ? [] : addOnList,
      userRoleList: isUserBased ? userRolesList : [],
      roles: formData.subscriptionRoles,
    };
    delete data.subscriptionRoles;
    if (isUserBased) delete data.roles;
    subscriptionApi.update(data, selectedSubscription.id).then(() => {
      subscriptionQuery.refetch();
      toast.success('Subscription Updated Successfully!');
    });
    setShowModal(false);
  };

  const handleDeleteSubscription = async () => {
    subscriptionApi
      .getTenantListBySubscription(selectedSubscription.id)
      .then((res) => {
        setUsedByTenantList(res.tenants);
      });
    setShowConfirmationModal(true);
  };

  const handleDelete = async () => {
    subscriptionApi.delete(selectedSubscription.id).then(() => {
      subscriptionQuery.refetch();
    });
    setShowModal(false);
  };

  const handleToggle = (id: number) => {
    const updatedFeaturesList = initialFeatures.map((ele: any) => {
      if (ele.id === id) {
        ele.is_enabled = !ele.is_enabled;
      }
      return ele;
    });
    setInitialFeatures(updatedFeaturesList);
  };

  const handleAddOnToggle = () => {
    setAddOnToggle(!addOnToggle);
  };

  const handleCreateNewAddOn = () => {
    setShowAddOnModal(true);
  };

  const handleOnCheckAddOn = (id: number, tempId: number) => {
    const updatedAddOnList = addOnList.map((ele: any) => {
      if ((ele.id && ele.id === id) || (ele.tempId && ele.tempId === tempId)) {
        ele.is_enabled = !ele.is_enabled;
      }
      return ele;
    });
    setAddOnList(updatedAddOnList);
  };

  const handleAddUserRole = () => {
    setShowUserRoleModal(true);
  };

  return (
    <>
      <FormModal
        modalProps={{
          title: isEditAction
            ? 'Edit Subscription Plan'
            : 'Create Subscription Plan',
          style: { maxWidth: '60rem' },
        }}
        onSubmit={(formData) =>
          isEditAction ? handleEdit(formData) : handleCreate(formData)
        }
        onCancel={() => setShowModal(false)}
        showDelete={isEditAction ? true : false}
        onDelete={handleDeleteSubscription}
      >
        <FormSchemaFields
          schema={getSchema(windowWidth, !isUserBased, mappingRoles)}
          formData={formContext.value}
          onChange={formContext.set}
          initialFormData={isEditAction ? selectedSubscription : {}}
        />
        <div className='subscription-features-container'>
          <h3>Included Features</h3>
          {initialFeatures.length > 0 && (
            <>
              <Features
                title='White Label'
                featuresList={initialFeatures.filter(
                  (f: any) => f.feature_type === 'white_label',
                )}
                handleToggle={handleToggle}
              />
              <Features
                title='Precipitation Tracking'
                featuresList={initialFeatures.filter(
                  (f: any) => f.feature_type === 'precipitation_tracking',
                )}
                handleToggle={handleToggle}
              />
              <Features
                title='Permission Group'
                featuresList={initialFeatures.filter(
                  (f: any) => f.feature_type === 'permission_group',
                )}
                handleToggle={handleToggle}
              />
              <Features
                title='Site Map Editor'
                featuresList={initialFeatures.filter(
                  (f: any) => f.feature_type === 'site_map_editor',
                )}
                handleToggle={handleToggle}
              />
              <Features
                title='Reporting'
                featuresList={initialFeatures.filter(
                  (f: any) => f.feature_type === 'reporting',
                )}
                handleToggle={handleToggle}
              />
              <Features
                title='Project / Findings'
                featuresList={initialFeatures.filter(
                  (f: any) => f.feature_type === 'projects_findings',
                )}
                handleToggle={handleToggle}
              />
              <Features
                title='System Settings'
                featuresList={initialFeatures.filter(
                  (f: any) => f.feature_type === 'system_settings',
                )}
                handleToggle={handleToggle}
              />
            </>
          )}
        </div>
        {isUserBased ? (
          <div className='subscription-user-roles-container'>
            <div className='subscription-user-roles-container__header'>
              <h3>User Roles</h3>
              <ActionButtons
                primary='Add User Role'
                onClick={handleAddUserRole}
              />
            </div>
            <div className='subscription-user-roles-list-container'>
              {userRolesList.map((ele: any, index: number) => {
                return (
                  !ele.is_deleted && (
                    <div className='subscription-user-roles-list' key={index}>
                      <div className='subscription-user-roles-list__name'>
                        <div className='subscription-user-roles-list__name__title'>
                          <h3
                            style={{
                              margin: '0px',
                            }}
                          >
                            {getRoleName(ele.role_id)}
                          </h3>
                          {ele.billing_type === 'on_demand' && (
                            <Tooltip
                              title='This feature will incur charges based on its demand.'
                              placement='top'
                              arrow
                              enterTouchDelay={0}
                            >
                              <div className='subscription-user-roles-list__name__tag'>
                                On Demand
                              </div>
                            </Tooltip>
                          )}
                        </div>
                        <h4 style={{ padding: '0px' }}>{ele.description}</h4>
                      </div>
                      <div className='subscription-user-roles-list__price'>
                        ${ele.price}
                      </div>
                      <span
                        onClick={() => setShowUserRoleModal(true)}
                        className='subscription-user-roles-list__edit'
                      >
                        <FontAwesomeIcon
                          icon={faEdit}
                          title='Edit Add On'
                          onClick={() => {
                            setShowUserRoleModal(true);
                            setSelectedAddUserRoleModalDetails(ele);
                          }}
                        />
                      </span>
                    </div>
                  )
                );
              })}
            </div>
          </div>
        ) : (
          <div className='subscription-add-on-container'>
            <div className='subscription-add-on-container__header'>
              <h3>Add On</h3>
              <ToggleSmall
                isChecked={addOnToggle}
                handleChange={handleAddOnToggle}
              />
            </div>
            {addOnToggle && (
              <div className='subscription-add-on-list-container'>
                {addOnList.map((ele: any, index: number) => {
                  return (
                    !ele.is_deleted && (
                      <div className='subscription-add-on-list' key={index}>
                        <div className='subscription-add-on-list__checkbox'>
                          <input
                            type='checkbox'
                            defaultChecked={ele.is_enabled}
                            onClick={() =>
                              handleOnCheckAddOn(ele.id, ele.tempId)
                            }
                          />
                        </div>
                        <div className='subscription-add-on-list__name'>
                          <div className='subscription-add-on-list__name__title'>
                            <h3
                              style={{
                                margin: '0px',
                              }}
                            >
                              {ele.name}
                            </h3>
                            {ele.as_per_project_usage &&
                              ele.as_per_project_usage != 'false' && (
                                <Tooltip
                                  title='This feature will incur charges based on its usage across projects. Charges will be calculated per project, based on the extent of usage of this feature.'
                                  placement='top'
                                  arrow
                                  enterTouchDelay={0}
                                >
                                  <div className='subscription-add-on-list__name__tag'>
                                    As per Project Usage
                                  </div>
                                </Tooltip>
                              )}
                          </div>
                          <h4 style={{ padding: '0px' }}>{ele.description}</h4>
                        </div>
                        <div className='subscription-add-on-list__price'>
                          ${ele.price}
                        </div>
                        <span
                          onClick={() => setShowAddOnModal(true)}
                          className='subscription-add-on-list__edit'
                        >
                          <FontAwesomeIcon
                            icon={faEdit}
                            title='Edit Add On'
                            onClick={() => {
                              setShowAddOnModal(true);
                              const newEle = {
                                ...ele,
                                feature_ids: ele.feature_ids.map((id: number) => ({
                                  value: id,
                                })),
                              }
                              setSelectedAddOnModalDetails(newEle);
                            }}
                          />
                        </span>
                      </div>
                    )
                  );
                })}
                <ActionButtons
                  primary='Create New Add On +'
                  onClick={handleCreateNewAddOn}
                />
              </div>
            )}
          </div>
        )}
      </FormModal>
      <Modal
        show={showConfirmationModal}
        handleClose={() => setShowConfirmationModal(false)}
        handleSubmit={handleDelete}
        title='Are you sure you want to delete this Subscription Plan?'
        subTitle='This cannot be undone.'
        disableSubmit={usedByTenantList.length === 0 ? false : true}
        submitBtnText='Delete Plan'
      >
        {usedByTenantList.length !== 0 && (
          <>
            <p style={{ marginLeft: '1.5rem' }}>
              This Subscription Plan is currently being used by the following
              tenants:
            </p>
            <ol>
              {usedByTenantList.map((ele: any, index: number) => (
                <li key={index}>{ele.name}</li>
              ))}
            </ol>
          </>
        )}
      </Modal>
    </>
  );
}
